const ProductsGrid = () => {
  const products = [
    {
      id: 1,
      name: "DownloadChow",
      tagline: "Food Delivery Platform",
      description: "Revolutionary food delivery app connecting restaurants with customers through seamless ordering and real-time tracking.",
      category: "Food Tech",
      icon: "🍔",
      features: ["Real-time tracking", "Multiple payment options", "Restaurant dashboard", "Review system", "Order history"],
      status: "Live",
      color: "from-red-100 to-red-50",
      textColor: "text-red-700"
    },
    {
      id: 2,
      name: "Tally Ride",
      tagline: "Ride-Hailing Service",
      description: "Advanced ride-hailing platform offering safe, reliable, and affordable transportation with smart routing algorithms.",
      category: "Transportation",
      icon: "🚗",
      features: ["Live tracking", "Driver ratings", "Safety features", "Multiple vehicle types", "Cashless payments"],
      status: "Live",
      color: "from-blue-100 to-blue-50",
      textColor: "text-blue-700"
    },
    {
      id: 3,
      name: "TallyERP 360",
      tagline: "Enterprise Resource Planning",
      description: "Comprehensive ERP solution for businesses of all sizes, streamlining operations with integrated modules and real-time analytics.",
      category: "Business Software",
      icon: "📊",
      features: ["Inventory management", "Accounting", "HR management", "CRM", "Reporting"],
      status: "Live",
      color: "from-green-100 to-green-50",
      textColor: "text-green-700"
    },
    {
      id: 4,
      name: "Tally Pay",
      tagline: "Payment Gateway",
      description: "Secure and reliable payment processing solution for online businesses with fraud detection and multi-currency support.",
      category: "FinTech",
      icon: "💳",
      features: ["Secure transactions", "Multi-currency", "Fraud detection", "Recurring billing", "Analytics"],
      status: "Beta",
      color: "from-purple-100 to-purple-50",
      textColor: "text-purple-700"
    },
    {
      id: 5,
      name: "Tally Connect",
      tagline: "Business Communication",
      description: "Unified communication platform for businesses with video conferencing, team chat, and project management tools.",
      category: "Collaboration",
      icon: "💬",
      features: ["Video calls", "Team chat", "File sharing", "Screen sharing", "Calendar integration"],
      status: "Coming Soon",
      color: "from-orange-100 to-orange-50",
      textColor: "text-orange-700"
    },
    {
      id: 6,
      name: "Tally Analytics",
      tagline: "Business Intelligence",
      description: "Powerful analytics platform providing actionable insights through data visualization and predictive analytics.",
      category: "Analytics",
      icon: "📈",
      features: ["Data visualization", "Predictive analytics", "Custom reports", "Real-time dashboards", "API integration"],
      status: "Live",
      color: "from-indigo-100 to-indigo-50",
      textColor: "text-indigo-700"
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      "Live": "bg-green-100 text-green-800",
      "Beta": "bg-yellow-100 text-yellow-800",
      "Coming Soon": "bg-gray-100 text-gray-800"
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {status}
      </span>
    );
  };

  return (
    <section className="section-padding">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Our Product Portfolio</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Discover our suite of innovative products designed to solve real-world problems and enhance business operations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border border-gray-100">
              {/* Product Header */}
              <div className={`p-6 ${product.color} border-b`}>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl">{product.icon}</div>
                    <div>
                      <h3 className="text-2xl font-bold">{product.name}</h3>
                      <p className="text-gray-600">{product.tagline}</p>
                    </div>
                  </div>
                  {getStatusBadge(product.status)}
                </div>
                <span className={`inline-block px-3 py-1 ${product.textColor} ${product.color.split(' ')[0].replace('from-', 'bg-')} rounded-full text-sm font-semibold`}>
                  {product.category}
                </span>
              </div>

              {/* Product Body */}
              <div className="p-6">
                <p className="text-gray-600 mb-6">{product.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <svg className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex space-x-3">
                  <button className="flex-1 btn-primary">
                    Learn More
                  </button>
                  <button className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* DownloadChow & Tally Ride Highlight Section */}
        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          {/* DownloadChow Highlight */}
          <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-lg border border-red-100">
            <div className="flex items-center mb-6">
              <div className="text-4xl mr-4">🍔</div>
              <div>
                <h3 className="text-2xl font-bold text-red-700">DownloadChow</h3>
                <p className="text-red-600">Leading Food Delivery Platform</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              DownloadChow has revolutionized food delivery in urban areas, connecting thousands of restaurants with millions of customers. Our AI-powered recommendation system and efficient delivery network ensure the best experience.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-white rounded-xl">
                <div className="text-2xl font-bold text-red-600">500K+</div>
                <div className="text-sm text-gray-600">Active Users</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl">
                <div className="text-2xl font-bold text-red-600">10K+</div>
                <div className="text-sm text-gray-600">Restaurant Partners</div>
              </div>
            </div>
            <a
              href="https://www.downloadchow.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
            >
              <button className="w-full bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors">
                Visit DownloadChow
              </button>
            </a>
          </div>

          {/* Tally Ride Highlight */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100">
            <div className="flex items-center mb-6">
              <div className="text-4xl mr-4">🚗</div>
              <div>
                <h3 className="text-2xl font-bold text-blue-700">Tally Ride</h3>
                <p className="text-blue-600">Smart Transportation Solution</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Tally Ride provides safe, reliable, and affordable transportation across cities. With advanced routing algorithms and safety features, we're making urban mobility smarter and more accessible for everyone.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-white rounded-xl">
                <div className="text-2xl font-bold text-blue-600">1M+</div>
                <div className="text-sm text-gray-600">Rides Completed</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl">
                <div className="text-2xl font-bold text-blue-600">50K+</div>
                <div className="text-sm text-gray-600">Driver Partners</div>
              </div>
            </div>
            <a
              href="https://www.tallyride.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
            >
              <button className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors">
                Try Tally Ride
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsGrid;